// Implémentation Sentry de SyncObserver : traduit chaque évènement du cycle de
// sync en signal Sentry, avec une sévérité graduée. C'est la seule glue entre
// le package offline (pur) et Sentry.
//
// Sévérités :
//  - enqueue / flush / retry réseau → breadcrumbs (contexte, pas d'alerte)
//  - conflit → error (divergence client/serveur)
//  - upload épuisé → error (photos jamais arrivées au serveur)
//  - purge → fatal (mutation perdue après MAX_RETRY : la donnée terrain ne
//    remontera jamais — l'alerte #1).
import * as Sentry from "@sentry/react";
import type { SyncObserver } from "@relais/offline";

function crumb(
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info",
): void {
  Sentry.addBreadcrumb({ category: "sync", message, data, level });
}

export const sentrySyncObserver: SyncObserver = {
  onEnqueue: (e) => crumb("sync.enqueue", { kind: e.kind, task_id: e.taskId }),

  onFlushStart: (e) => crumb("sync.flush.start", { batch_size: e.batchSize }),

  onFlushEnd: (e) =>
    crumb("sync.flush.end", {
      processed: e.processed,
      failed: e.failed,
      conflicts: e.conflicts,
      completed: e.completed.length,
    }),

  onNetworkError: (e) =>
    crumb(
      "sync.network_error",
      { phase: e.phase, message: e.message, batch_size: e.batchSize },
      "warning",
    ),

  onConflict: (e) => {
    crumb("sync.conflict", { kind: e.kind, task_id: e.taskId, reason: e.reason }, "error");
    Sentry.captureMessage("sync.conflict", {
      level: "error",
      tags: { sync_event: "conflict", mutation_kind: e.kind },
      fingerprint: ["sync", "conflict", e.kind],
      extra: { task_id: e.taskId, reason: e.reason },
    });
  },

  onPurge: (e) => {
    // Perte de mutation après MAX_RETRY — sévérité maximale.
    Sentry.captureMessage("sync.queue.purged", {
      level: "fatal",
      tags: { sync_event: "purge", mutation_kind: e.kind },
      fingerprint: ["sync", "purge", e.kind],
      extra: { task_id: e.taskId, retry_count: e.retryCount, reason: e.reason },
    });
  },

  onTasksPurged: (e) =>
    crumb("sync.tasks_purged", { task_ids: e.taskIds, count: e.taskIds.length }),

  onUploadError: (e) => {
    if (e.exhausted) {
      Sentry.captureMessage("sync.upload.exhausted", {
        level: "error",
        tags: { sync_event: "upload_exhausted" },
        fingerprint: ["sync", "upload", "exhausted"],
        extra: {
          task_id: e.taskId,
          media_id: e.mediaId,
          retry_count: e.retryCount,
          message: e.message,
        },
      });
    } else {
      crumb(
        "sync.upload.retry",
        { task_id: e.taskId, media_id: e.mediaId, retry_count: e.retryCount },
        "warning",
      );
    }
  },
};
